import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate(tenantPath('/login'));
    }
  }, [token, navigate, tenantPath]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const pwErrors = getPasswordComplexityErrors(formData.password);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setIsLoading(true);
      await api.post('/auth/reset-password', {
        token,
        new_password: formData.password
      });
      
      toast.success('Password reset successfully!');
      navigate(tenantPath('/login'));
    } catch (error) {
      console.error('Reset password error:', error);
      const message = error.response?.data?.detail || 'Password reset failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!token) return null;
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <img src="/logo.png" alt="Golfshop.fit" className="h-20 w-20 mx-auto mb-4 rounded-full object-cover" data-testid="reset-password-title" />
          <p className="text-gray-600 text-lg">Create a new password</p>
        </div>
        
        <div className="bg-gray-50 p-8 rounded-lg border border-[#F3F4F6]">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-gray-600 text-sm mb-2 block">
                New Password
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="••••••••"
                data-testid="password-input"
              />
              <p className="text-gray-500 text-xs mt-1">{PASSWORD_HINT}</p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-gray-600 text-sm mb-2 block">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
          
          <Link 
            to={tenantPath('/login')}
            className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-900 text-sm mt-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to login</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
